import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, reflectComponentType, ApplicationRef, createComponent, EnvironmentInjector, InjectionToken, inject, Injector, Injectable, ViewContainerRef, TemplateRef, Directive } from '@angular/core';
import { injectElement, isString, isViewContainer, isTemplateRef, isComponent, hasChanged } from '@odx/angular/utils';
import { isObservable, distinctUntilChanged } from 'rxjs';
import { deepmerge } from '@odx/angular/internal';
import { DOCUMENT } from '@angular/common';

class DynamicViewComponent {
    constructor() {
        this.element = injectElement();
        this.currentSubscription = null;
    }
    set content(value) {
        if (isObservable(value)) {
            this.ngOnDestroy();
            this.currentSubscription = value.pipe(distinctUntilChanged()).subscribe((innerValue) => {
                this.setContent(innerValue);
            });
        }
        else {
            this.setContent(value);
        }
    }
    ngOnDestroy() {
        this.currentSubscription?.unsubscribe();
        this.currentSubscription = null;
    }
    setContent(value) {
        this.element.nativeElement.innerHTML = value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DynamicViewComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: DynamicViewComponent, isStandalone: true, selector: "odx-dynamic-view", inputs: { content: "content" }, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DynamicViewComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'odx-dynamic-view',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    encapsulation: ViewEncapsulation.None,
                    standalone: true,
                }]
        }], propDecorators: { content: [{
                type: Input
            }] } });

/**
 * Prepares an array of node arrays suitable for projection into the `<ng-content>` slots
 * of a dynamically created Angular component based on its content selectors.
 *
 * The function inspects the component's content selectors and aligns the provided nodes
 * for projection according to these selectors. A selector of '*' indicates that the nodes
 * should be projected into this slot. If a content selector does not match '*', an empty
 * array is returned for that slot, indicating no nodes are projected into it.
 *
 * @param {Type<unknown>} component - The component class for which the projectable nodes are being prepared.
 * @param {Node[]} nodes - The nodes intended for projection into the component's `<ng-content>` slots.
 * @returns {Node[][]} An array of node arrays, each corresponding to a `<ng-content>` slot in the component.
 * Each sub-array contains nodes to be projected into the respective `<ng-content>` slot, based on the component's content selectors.
 *
 * @example
 * ```ts
 * // Given a component with two `<ng-content>` slots, the first without a selector and the second with the selector `<ng-content select=".selector-class">`,
 * const nodes = [document.createTextNode('Hello'), document.createElement('div')];
 * const projectableNodes = createProjectableNodes(MyComponent, nodes);
 * // projectableNodes would be `[[TextNode, DivElement], []]` for a component with selectors ['*', '.selector-class']
 * ```
 */
function createProjectableNodes(component, nodes) {
    const componentType = reflectComponentType(component);
    if (componentType && componentType.ngContentSelectors.length > 0) {
        return componentType.ngContentSelectors.map((selector) => (selector === '*' ? nodes : []));
    }
    return [];
}

/**
 * Type guard function to check if a given value is considered dynamic text content.
 * Dynamic text content can either be a plain string or an Observable. This function
 * facilitates type-safe handling of dynamic text content by checking the type of the value.
 *
 * @param {unknown} value - The value to be checked.
 * @returns {value is DynamicTextContent} Returns `true` if the value is a string or an Observable,
 * indicating it can be used as dynamic text content; otherwise, returns `false`.
 *
 * @example
 * ```ts
 * // Example usage of the isDynamicTextContent function
 * const textValue: unknown = getTextValue(); // This function may return either string or Observable
 *
 * if (isDynamicTextContent(textValue)) {
 *   // The type of textValue is now narrowed to DynamicTextContent (string | Observable)
 *   console.log('The value can be used as dynamic text content.');
 * } else {
 *   console.log('The value is not suitable as dynamic text content.');
 * }
 * ```
 */
function isDynamicTextContent(value) {
    return isString(value) || isObservable(value);
}

/**
 * The DynamicComponentRef class is a powerful utility for dynamically creating and managing
 * Angular components at runtime. It encapsulates the logic for component creation, input handling,
 * and lifecycle management, making it easier to work with dynamic components in complex applications.
 *
 * A reference to a dynamically created component, providing methods to manage its lifecycle,
 * detect changes, and update its inputs based on a context.
 *
 * @template T - The type of the component being managed.
 * @template C - The type of the context used to update the component's inputs.
 */
class DynamicComponentRef {
    constructor(options) {
        this.options = options;
        this.application = this.options.injector.get(ApplicationRef);
        this.componentRef = null;
        this.componentInputs = null;
        const { component, context, injector, projectableNodes, host } = options;
        this.componentRef = createComponent(component, {
            environmentInjector: injector.get(EnvironmentInjector),
            elementInjector: injector,
            projectableNodes,
        });
        this.componentInputs =
            reflectComponentType(this.componentRef.componentType)?.inputs.reduce((acc, { propName }) => {
                acc[propName] = true;
                return acc;
            }, {}) ?? null;
        if (isViewContainer(host)) {
            host.insert(this.componentRef.hostView);
        }
        else {
            this.application.attachView(this.componentRef.hostView);
            if (host) {
                host.appendChild(this.getElement());
            }
        }
        if (context) {
            this.update(context);
        }
    }
    /**
     * Triggers change detection for the dynamically created component, ensuring that any updates to inputs
     * or internal state are reflected in the view.
     */
    detectChanges() {
        this.componentRef?.changeDetectorRef.detectChanges();
    }
    /**
     * Destroys the dynamically created component, cleaning up resources and detaching it from the application view.
     */
    destroy() {
        if (this.componentRef?.hostView.destroyed) {
            this.componentRef = null;
            return;
        }
        if (this.componentRef && !isViewContainer(this.options.host)) {
            this.application.detachView(this.componentRef.hostView);
        }
        this.componentRef?.destroy();
        this.componentRef = null;
        this.componentInputs = null;
    }
    /**
     * Retrieves the native element associated with the dynamically created component's host view.
     *
     * @returns {Element} The native DOM element of the component.
     */
    getElement() {
        return this.componentRef?.location.nativeElement ?? null;
    }
    /**
     * Updates the component's inputs based on the provided context. Merges the new context with the existing
     * context and applies the combined context as inputs to the component.
     *
     * @param {C} context - The context containing new values for the component's inputs.
     */
    update(context) {
        this.options.context = deepmerge({}, this.options.context, context);
        if (!this.componentRef)
            return;
        for (const key in this.options.context) {
            if (this.componentInputs?.[key]) {
                this.componentRef.setInput(key, this.options.context[key]);
            }
        }
    }
    /**
     * Retrieves the current context used for the component's inputs.
     *
     * @returns {GetDynamicViewContext<T>} The current context object.
     */
    getContext() {
        return this.options.context ?? {};
    }
    /**
     * Retrieves the view reference associated with the dynamically created component.
     *
     * @returns {ViewRef | null} The view reference, or null if the component has not been created or has been destroyed.
     */
    getView() {
        return this.componentRef?.hostView ?? null;
    }
}

/**
 * A reference to a dynamically created template, offering methods to manage its lifecycle,
 * detect changes, and update its context. It encapsulates the logic for creating and manipulating
 * an `EmbeddedViewRef` from a given `TemplateRef`.
 *
 * @template T - The type of the `NgTemplateRef` being managed.
 */
class DynamicTemplateRef {
    constructor(options) {
        this.options = options;
        this.application = this.options.injector.get(ApplicationRef);
        this.embeddedViewRef = null;
        this.create(options);
    }
    /**
     * Triggers change detection on the embedded view created from the template, ensuring that any updates
     * to the context or internal state are reflected in the view.
     */
    detectChanges() {
        this.embeddedViewRef?.detectChanges();
    }
    /**
     * Destroys the embedded view associated with the template, cleaning up resources and detaching it from the
     * application view if necessary.
     */
    destroy() {
        if (this.embeddedViewRef?.destroyed) {
            this.embeddedViewRef = null;
            return;
        }
        if (!isViewContainer(this.options.host) && this.embeddedViewRef) {
            this.application.detachView(this.embeddedViewRef);
        }
        this.embeddedViewRef?.destroy();
        this.embeddedViewRef = null;
    }
    /**
     * Retrieves the native element associated with the template's embedded view's root node.
     *
     * @returns {Element} The native DOM element of the embedded view's root node, if available.
     */
    getElement() {
        return this.embeddedViewRef?.rootNodes?.[0] ?? null;
    }
    /**
     * Updates the context of the embedded view based on the provided context object. Merges the new context
     * with the existing one and applies the combined context to the embedded view.
     *
     * @param {GetDynamicViewContext<T>} context - The context containing new values for the template's view.
     */
    update(context) {
        this.options.context = deepmerge({}, this.options.context, context);
        if (this.embeddedViewRef) {
            this.embeddedViewRef.context = this.options.context;
            this.detectChanges();
        }
    }
    /**
     * Retrieves the current context used for the template's view.
     *
     * @returns {GetDynamicViewContext<T>} The current context object.
     */
    getContext() {
        return this.options.context ?? {};
    }
    /**
     * Retrieves the view reference (`ViewRef`) associated with the dynamically created template's embedded view.
     *
     * @returns {ViewRef | null} The view reference, or null if the template has not been instantiated or has been destroyed.
     */
    getView() {
        return this.embeddedViewRef;
    }
    create({ context, template, host }) {
        if (isViewContainer(host)) {
            this.embeddedViewRef = host.createEmbeddedView(template, context, { injector: this.options.injector });
        }
        else {
            this.embeddedViewRef = template.createEmbeddedView(context ?? {}, this.options.injector);
            this.application.attachView(this.embeddedViewRef);
        }
    }
}

/**
 * Type guard function to check if a given value is a reference to a dynamically created view.
 * This helps in distinguishing between dynamic view references (`DynamicComponentRef` or `DynamicTemplateRef`)
 * and other types of values, facilitating type-safe operations on dynamic views.
 *
 * @param {unknown} value - The value to check.
 * @returns {value is DynamicViewRef<DynamicContent>} `true` if the value is an instance of `DynamicComponentRef`
 * or `DynamicTemplateRef`, indicating it is a dynamic view reference; otherwise, `false`.
 *
 * @example
 * ```ts
 * // Given a value that could be a dynamic view reference or something else
 * const value: unknown = getSomeValue();
 *
 * if (isDynamicView(value)) {
 *   // TypeScript now knows `value` is a dynamic view reference, so you can access its methods safely
 *   value.detectChanges();
 * } else {
 *   console.log('Value is not a dynamic view reference');
 * }
 * ```
 */
function isDynamicView(value) {
    return value instanceof DynamicComponentRef || value instanceof DynamicTemplateRef;
}

/**
 * @internal
 * Injection token for the dynamic view context.
 */
const DYNAMIC_VIEW_CONTEXT = new InjectionToken('@odx/angular/cdk/dynamic-view::DynamicViewContext');

/**
 * @internal
 * Injection token for the default host element for dynamic views.
 */
const ODX_DYNAMIC_VIEW_DEFAULT_HOST = new InjectionToken('@odx/angular/cdk/dynamic-view::DynamicViewDefaultHost', {
    providedIn: 'root',
    factory: () => inject(DOCUMENT).body,
});
/**
 * @internal
 * Retrieves the default host for dynamic views.
 *
 * @returns {Element} The default host element for dynamic views.
 */
function getDynamicViewDefaultHost() {
    return inject(ODX_DYNAMIC_VIEW_DEFAULT_HOST);
}

/**
 * A service for creating and managing dynamic views in an Angular application.
 * It supports rendering Angular components, templates, and text content dynamically,
 * allowing for flexible and dynamic content composition.
 */
class DynamicViewService {
    constructor() {
        this.injector = inject(Injector);
        this.defaultHost = getDynamicViewDefaultHost();
    }
    /**
     * Creates and returns a reference to a dynamic view based on the provided content and options.
     * The method supports dynamic creation of components, template references, and text content,
     * providing a unified API for dynamic content rendering.
     *
     * @template T - The type of the content to be dynamically rendered.
     * @param {T} content - The content to be rendered dynamically. This can be a component class,
     * a template reference, or text content.
     * @param {Partial<DynamicViewOptions<T>>} [options={}] - Optional configuration for the dynamic view creation,
     * including a custom injector, context, host element, and additional providers for the dynamic component's injector.
     * @returns {DynamicViewRef<T>} A reference to the dynamically created view, encapsulating the rendered content
     * and providing methods for interaction and management.
     * @throws {Error} Throws an error if the content type is not supported.
     * @example
     * ```ts
     * // Create a dynamic view for a component
     * const componentRef = dynamicViewService.createView(MyComponent, { context: { ... } });
     *
     * // Create a dynamic view for a template reference
     * const templateRef = dynamicViewService.createView(myTemplate, { context: { ... } });
     *
     * // Create a dynamic view for text content
     * const textRef = dynamicViewService.createView('Dynamic text content', {});
     * ```
     */
    createView(content, options = {}) {
        const viewOptions = {
            ...options,
            host: options.host ?? this.defaultHost,
            injector: Injector.create({
                providers: [{ provide: DYNAMIC_VIEW_CONTEXT, useValue: options.context }, options.providers ?? []],
                parent: options.injector ?? this.injector,
            }),
        };
        if (isTemplateRef(content)) {
            return new DynamicTemplateRef({ ...viewOptions, template: content });
        }
        if (isComponent(content)) {
            return new DynamicComponentRef({ ...viewOptions, context: options.context, component: content });
        }
        if (isDynamicTextContent(content)) {
            return new DynamicComponentRef({
                ...viewOptions,
                component: DynamicViewComponent,
                context: { content },
            });
        }
        throw new Error('Content type is not supported');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DynamicViewService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DynamicViewService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DynamicViewService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * A directive that dynamically renders content based on the provided input. It supports rendering
 * Angular components, templates, and text content, allowing for dynamic and flexible content
 * management within Angular applications.
 *
 * @template T - A type parameter that extends `DynamicContent<unknown>`, representing the type of content to be dynamically rendered.
 */
class DynamicViewDirective {
    constructor() {
        this.dynamicViewService = inject(DynamicViewService);
        this.viewContainer = inject(ViewContainerRef);
        this.template = inject(TemplateRef);
        this.viewRef = null;
        this.contentViewRef = null;
        /**
         * The content to be dynamically rendered. This can be a component, template reference, or text content.
         * The content is wrapped in a `DynamicViewRef`, providing additional control over the dynamically rendered view.
         *
         * @type {T | DynamicViewRef<T> | null}
         * @default null
         */
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.content = null;
    }
    ngOnInit() {
        this.render();
    }
    ngOnChanges(changes) {
        if (hasChanged(changes, 'injector')) {
            this.render();
        }
        if (hasChanged(changes, 'content')) {
            const hasChangedTextContent = isDynamicTextContent(changes.content?.previousValue) && isDynamicTextContent(changes.content?.currentValue);
            this.render(!hasChangedTextContent);
        }
        if (hasChanged(changes, 'context')) {
            this.viewRef?.update(changes.context?.currentValue ?? {});
        }
    }
    ngOnDestroy() {
        this.destroy();
    }
    /**
     * Retrieves the current `DynamicViewRef` associated with the rendered content, providing access
     * to the view reference for additional operations like updating context or manual destruction.
     *
     * @returns {DynamicViewRef<T> | null} The current view reference for the dynamically rendered content, if available.
     */
    getView() {
        return this.viewRef;
    }
    render(reRender = true) {
        if (!this.content) {
            this.destroy();
            return;
        }
        if (!reRender && this.viewRef) {
            this.viewRef?.update({ content: this.content });
            return;
        }
        this.viewRef?.destroy();
        if (isDynamicView(this.content)) {
            this.viewRef = this.content;
            const hostView = this.viewRef.getView();
            if (hostView) {
                this.viewContainer.insert(hostView);
            }
        }
        else {
            let projectableNodes = [];
            if (isComponent(this.content)) {
                this.contentViewRef = this.dynamicViewService.createView(this.template, { injector: this.injector });
                const rootNodes = this.contentViewRef.getView().rootNodes;
                projectableNodes = createProjectableNodes(this.content, rootNodes);
            }
            this.viewRef = this.dynamicViewService.createView(this.content, {
                context: this.context ?? undefined,
                injector: this.injector ?? this.viewContainer.injector,
                projectableNodes,
                host: this.viewContainer,
            });
        }
    }
    destroy() {
        this.contentViewRef?.destroy();
        this.viewRef?.destroy();
        this.contentViewRef = null;
        this.viewRef = null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DynamicViewDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: DynamicViewDirective, isStandalone: true, selector: "ng-template[odxDynamicView]", inputs: { content: ["odxDynamicView", "content"], injector: ["odxDynamicViewInjector", "injector"], context: ["odxDynamicViewContext", "context"] }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DynamicViewDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ng-template[odxDynamicView]',
                    standalone: true,
                }]
        }], propDecorators: { content: [{
                type: Input,
                args: ['odxDynamicView']
            }], injector: [{
                type: Input,
                args: ['odxDynamicViewInjector']
            }], context: [{
                type: Input,
                args: ['odxDynamicViewContext']
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { DYNAMIC_VIEW_CONTEXT, DynamicComponentRef, DynamicTemplateRef, DynamicViewComponent, DynamicViewDirective, DynamicViewService, ODX_DYNAMIC_VIEW_DEFAULT_HOST, createProjectableNodes, getDynamicViewDefaultHost, isDynamicTextContent, isDynamicView };
